import { motion } from 'framer-motion';
import { css, keyframes, styled } from 'styled-components';

export const Wrapper = styled(motion.div)<{ $width?: number; $isHovering?: boolean }>`
    position: relative;
    transition: transform 0.3s linear;

    ${({ $width }) =>
        $width &&
        css`
            --liquidid-size: ${$width}px;
            width: var(--liquidid-size);
            height: var(--liquidid-size);
        `}

    ${({ $isHovering }) =>
        $isHovering &&
        css`
            cursor: pointer;
            transform: scale(1.1);
        `}
`;

export const BackgroundPink = styled(motion.div)<{ $width: number }>`
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;

    width: 80%;
    height: 80%;

    background-color: rgba(250, 6, 255, 0.4);
    filter: blur(100px);
    border-radius: 100%;
    pointer-events: none;

    ${({ $width }) => {
        if ($width < 500) {
            return css`
                filter: blur(50px);
            `;
        }
    }}
`;

export const BackgroundPurple = styled(motion.div)<{ $width: number }>`
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 0;

    width: 80%;
    height: 80%;

    background-color: rgba(82, 61, 241, 1);

    filter: blur(100px);
    border-radius: 100%;
    pointer-events: none;

    ${({ $width }) => {
        if ($width < 500) {
            return css`
                filter: blur(50px);
            `;
        }
    }}
`;

export const Perspective = styled(motion.div)`
    perspective: calc(var(--liquidid-size) * 4);
    perspective-origin: 50% 50%;
    width: 100%;
    height: 100%;

    position: relative;
    z-index: 2;
`;

export const CubeWrapper = styled(motion.div)`
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transform-origin: 50% 50%;
`;

const spinAnimation = keyframes`
    0% {
        transform: rotateY(-360deg);
    }
    100% {
        transform: rotateY(0deg);
    }
`;

export const CubeInside = styled(motion.div)<{ $pause?: boolean; $freeze?: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
    transform-origin: 50% 50%;

    animation: ${spinAnimation} 30s linear infinite;

    animation-play-state: ${({ $pause }) => {
        return $pause ? 'paused' : 'running';
    }};

    ${({ $freeze }) =>
        $freeze &&
        css`
            animation: none;
        `}
`;

export const Face = styled.div<{ $face: 'front' | 'left' | 'back' | 'right' | 'top' | 'bottom'; $width: number }>`
    position: absolute;
    width: 100%;
    height: 100%;

    ${({ $face }) => {
        switch ($face) {
            case 'front':
                return css`
                    transform: translateZ(calc(var(--liquidid-size) / 2));
                `;
            case 'left':
                return css`
                    transform: rotateY(270deg) translateX(-50%);
                    transform-origin: center left;
                `;
            case 'back':
                return css`
                    transform: translateZ(calc((var(--liquidid-size) / 2) * -1)) rotateY(180deg);
                `;
            case 'right':
                return css`
                    transform: rotateY(-270deg) translateX(50%);
                    transform-origin: top right;
                `;
            case 'top':
                return css`
                    transform: rotateX(90deg) translateY(-50%);
                    transform-origin: top center;
                `;
            case 'bottom':
                return css`
                    transform: rotateX(-90deg) translateY(50%);
                    transform-origin: bottom center;
                `;
        }
    }}

    padding: 4px;
    //backdrop-filter: blur(5px);

    ${({ $width }) => {
        if ($width < 100) {
            return css`
                padding: 0px;
                backdrop-filter: none;
            `;
        }

        if ($width < 500) {
            return css`
                padding: 2px;
            `;
        }
    }}
`;

export const FaceInside = styled.div<{ $width: number }>`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    z-index: 0;

    background-color: rgba(9, 7, 25, 0.5);

    ${({ $width }) => {
        if ($width < 100) {
            return css`
                border-radius: 0px;
            `;
        }

        if ($width < 500) {
            return css`
                border-radius: 6px;
            `;
        }
    }}
`;

export const FaceOutside = styled.div<{ $width: number }>`
    position: relative;
    width: 100%;
    height: 100%;
    z-index: 1;

    box-shadow: inset 0px 0px calc(var(--liquidid-size) / 2) rgba(255, 255, 255, 0.25);

    border-radius: 10px;

    ${({ $width }) => {
        if ($width < 100) {
            return css`
                border-radius: 0px;
            `;
        }

        if ($width < 500) {
            return css`
                border-radius: 6px;
            `;
        }
    }}
`;

export const MediaHolder = styled.div<{ $width: number }>`
    width: 100%;
    height: 100%;
    border-radius: 10px;
    overflow: hidden;

    ${({ $width }) => {
        if ($width < 100) {
            return css`
                border-radius: 2px;
            `;
        }

        if ($width < 500) {
            return css`
                border-radius: 6px;
            `;
        }
    }}
`;

export const Image = styled.div<{ $url: string; $width: number }>`
    width: 100%;
    height: 100%;
    background-image: url(${({ $url }) => $url});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    border-radius: 10px;

    ${({ $width }) => {
        if ($width < 100) {
            return css`
                border-radius: 2px;
            `;
        }

        if ($width < 500) {
            return css`
                border-radius: 6px;
            `;
        }
    }}
`;

export const YatHand = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;

    transform: translateZ(0) rotateY(45deg);

    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 50%;
        opacity: 0.7;
    }
`;

/*

// These cause too much GPU usage

export const LightSource = styled.div`
    position: absolute;
    top: -10%;
    left: -10%;
    z-index: 2;

    width: 60%;
    height: 60%;

    background-color: #fff;
    mix-blend-mode: overlay;

    filter: blur(80px);
    border-radius: 100%;
    pointer-events: none;
`;

export const ShadowSource = styled.div`
    position: absolute;
    bottom: -10%;
    right: -10%;
    z-index: 2;

    width: 50%;
    height: 50%;

    background-color: rgba(9, 7, 25, 1);
    mix-blend-mode: overlay;

    filter: blur(80px);
    border-radius: 100%;
    pointer-events: none;
`;


export const CubeShadow = styled(motion.div)`
    position: absolute;
    width: 100%;
    height: 100%;

    transform: rotateX(90deg) translateY(70%);
    transform-origin: 0 120%;

    background-color: rgba(9, 7, 25, 0.5);
    filter: blur(30px);
`;


*/
